import { DirectionProvider } from '@radix-ui/react-direction'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { CookieConsent } from '@/components/CookieConsent'

import packageJson from '../package.json'
import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { useAppConfig } from './api/config/queries/useAppConfig'
//utils
import { currentAuthenticatedUser } from './api/user/userService'
import { LoadingScreen } from './components/LoadingScreen'
import { MaintenanceScreen } from './components/MaintenanceScreen'
import { ToastContainer, ToastProvider } from './components/ui/toast'
import Layout from './Layout'
import { clarityIdentify, claritySet } from './lib/clarity'
import { QUERY_KEYS } from './lib/queryKeys'
import NavBar from './NavBar'
import OfficeNavbar from './pages/office-side/OfficeNavbar'
import { RoutePath } from './RoutePath'
import { setIsAuthenticated } from './store/appSlice'

export default function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isReady, setIsReady] = useState(false)
  const { data: loggedUser } = useCurrentUser()
  const { data: appConfig, isLoading: isAppConfigLoading } = useAppConfig()
  const location = useLocation()
  const isOfficePage = location.pathname.startsWith('/office') || location.pathname.startsWith('/all-packages-view')
  const shouldShowLoading = !isReady || isAppConfigLoading

  useEffect(() => {
    // Update HTML lang attribute
    if (isOfficePage) {
      document.documentElement.lang = 'en'
    } else {
      document.documentElement.lang = 'he'
    }
  }, [isOfficePage])

  useEffect(() => {
    if (window.location.href.includes('office')) {
      const enableToolbar = document.getElementById('enable-toolbar')
      if (enableToolbar) enableToolbar.style.display = 'none'
    }
  }, [])

  useEffect(() => {
    //versioning
    const version = packageJson.version
    const last_version = localStorage.getItem(`version`)
    if (!last_version || last_version !== version) {
      localStorage.setItem(`version`, version)
      window.location.reload()
    }

    //user auth
    ;(async () => {
      const userData = await currentAuthenticatedUser()
      if (!userData) {
        localStorage.removeItem('cognito-tokens')
        dispatch(setIsAuthenticated(false))
        setIsReady(true)
        return
      }
      queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], userData)
      dispatch(setIsAuthenticated(true))
      if (location.pathname == '/') navigate(RoutePath.ClientPkg)
      else navigate(location.pathname + location.search + location.hash)
      setIsReady(true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set Sentry user context when user is logged in
  useEffect(() => {
    if (loggedUser?.Username) {
      Sentry.setUser({
        id: loggedUser.Username,
        email: loggedUser.UserAttributes?.email,
        username: loggedUser.UserAttributes?.name || loggedUser.Username,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [loggedUser])

  useEffect(() => {
    const customId = loggedUser?.Username ?? 'anonymous'
    clarityIdentify(customId)
    claritySet('environment', import.meta.env.MODE ?? 'development')
  }, [loggedUser?.Username])

  const hardRefresh = () => {
    localStorage.clear()
    navigate('/')
    window.location.reload()
  }

  return (
    <DirectionProvider dir={isOfficePage ? 'ltr' : 'rtl'}>
      <ToastProvider>
        <div className='h-[100dvh]'>
          {appConfig?.isMaintenance ? (
            <MaintenanceScreen />
          ) : shouldShowLoading ? (
            <LoadingScreen onRefresh={hardRefresh} />
          ) : (
            <>
              {loggedUser?.permissions['Admin'] ||
              loggedUser?.permissions['Agent'] ||
              loggedUser?.permissions['SeniorAgent'] ||
              loggedUser?.permissions['OfficeManager'] ||
              loggedUser?.permissions['Worker'] ||
              loggedUser?.permissions['PickupOrderDriver'] ? (
                <OfficeNavbar />
              ) : (
                <NavBar />
              )}
              <Layout />
            </>
          )}
        </div>
        <ToastContainer />
        <CookieConsent />
      </ToastProvider>
    </DirectionProvider>
  )
}
